//결제선생 영수증 모양을 canvas로 그려서 이미지(dataURL + Blob)로 만들어주는 공용 모듈
//등록인증 이벤트 페이지(1단계 영수증 저장)와 결제선생 영수증 페이지에서 같이 사용

export interface ReceiptBusinessInfo {
    name: string;
    ceo: string;
    businessNumber: string;
    phone: string;
    address: string;
}

export interface ReceiptData {
    apprState: string;
    paidAt: string;
    approvalNumber: string;
    price: number;
    payMethod: string;
    cardName: string;
    cardNumber: string;
    installment: string;
    business: ReceiptBusinessInfo | null;
    locationKorean: string;
}

const FONT_STACK = `"Pretendard", "Pretendard GOV", -apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif`;

const WIDTH = 375;
const PADDING_X = 24;
const ROW_HEIGHT = 46;
const LINE_HEIGHT = 20;

//주소처럼 긴 텍스트를 maxWidth 안에 들어가게 여러 줄로 나누기
const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number) => {
    const words = text.split(" ");
    const lines: string[] = [];
    let current = "";

    words.forEach((word) => {
        const tryLine = current ? current + " " + word : word;
        if (ctx.measureText(tryLine).width > maxWidth && current) {
            lines.push(current);
            current = word;
        } else {
            current = tryLine;
        }
    });

    if (current) {
        lines.push(current);
    }

    return lines.length > 0 ? lines : [text];
};

const loadImage = (src: string) => {
    return new Promise<HTMLImageElement | null>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = src;
    });
};

export const drawReceiptImage = async (data: ReceiptData, logoUrl: string): Promise<{ dataUrl: string; blob: Blob | null }> => {

    const valueFont = `500 14px ${FONT_STACK}`;
    const labelFont = `400 13px ${FONT_STACK}`;
    const sectionFont = `700 15px ${FONT_STACK}`;

    //결제정보 행 (값이 없는 행은 생략)
    const paymentRows: Array<[string, string]> = [];
    paymentRows.push(["결제상태", "결제완료"]);
    if (data.paidAt) paymentRows.push(["결제일시", data.paidAt]);
    if (data.approvalNumber) paymentRows.push(["승인번호", data.approvalNumber]);
    paymentRows.push(["결제금액", data.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "원"]);
    if (data.payMethod) paymentRows.push(["결제수단", data.payMethod]);
    if (data.cardName) paymentRows.push(["카드/기관명", data.cardName]);
    if (data.cardNumber) paymentRows.push(["카드번호", data.cardNumber]);
    if (data.installment) paymentRows.push(["할부개월", data.installment]);

    //사업자정보 행 (대표자/주소가 비어있으면 그 줄은 생략)
    const businessRows: Array<[string, string]> = [];
    if (data.business) {
        if (data.business.name) businessRows.push(["사업자명", data.business.name]);
        if (data.business.ceo) businessRows.push(["대표자", data.business.ceo]);
        if (data.business.businessNumber) businessRows.push(["사업자번호", data.business.businessNumber]);
        if (data.business.phone) businessRows.push(["전화번호", data.business.phone]);
        if (data.business.address) businessRows.push(["주소", data.business.address]);
    }

    //주소 줄 수 계산용 (canvas 크기를 정하기 전에 높이를 알아야 해서 먼저 측정)
    const measureCanvas = document.createElement("canvas");
    const measureCtx = measureCanvas.getContext("2d");

    const valueMaxWidth = WIDTH - PADDING_X * 2 - 70;

    const rowLines = (rows: Array<[string, string]>) => {
        return rows.map(([label, value]) => {
            if (!measureCtx) {
                return { label, lines: [value] };
            }
            measureCtx.font = valueFont;
            const lines = measureCtx.measureText(value).width > valueMaxWidth ? wrapText(measureCtx, value, valueMaxWidth) : [value];
            return { label, lines };
        });
    };

    const paymentRowInfos = rowLines(paymentRows);
    const businessRowInfos = rowLines(businessRows);

    const rowsHeight = (infos: Array<{ label: string, lines: string[] }>) => {
        return infos.reduce((acc, info) => acc + (info.lines.length > 1 ? 26 + info.lines.length * LINE_HEIGHT : ROW_HEIGHT), 0);
    };

    const sectionHeaderHeight = 34;
    const topPadding = 34;
    const sectionGap = 36;
    const zigzagGap = 30;
    const logoAreaHeight = 76;

    let totalHeight = topPadding + sectionHeaderHeight + rowsHeight(paymentRowInfos);
    if (businessRowInfos.length > 0) {
        totalHeight += sectionGap + sectionHeaderHeight + rowsHeight(businessRowInfos);
    }
    totalHeight += zigzagGap + logoAreaHeight;

    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = WIDTH * scale;
    canvas.height = Math.ceil(totalHeight) * scale;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
        return { dataUrl: "", blob: null };
    }

    ctx.scale(scale, scale);

    //배경
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, WIDTH, totalHeight);

    let y = topPadding;

    const drawSectionHeader = (title: string) => {
        ctx.font = sectionFont;
        ctx.fillStyle = "#191f28";
        ctx.textAlign = "left";
        ctx.textBaseline = "alphabetic";
        ctx.fillText(title, PADDING_X, y);

        y += 12;

        ctx.strokeStyle = "#191f28";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(PADDING_X, y);
        ctx.lineTo(WIDTH - PADDING_X, y);
        ctx.stroke();

        y += 22;
    };

    const drawRows = (infos: Array<{ label: string, lines: string[] }>) => {
        infos.forEach((info) => {
            const isMultiLine = info.lines.length > 1;
            const rowHeight = isMultiLine ? 26 + info.lines.length * LINE_HEIGHT : ROW_HEIGHT;

            //라벨 (왼쪽)
            ctx.font = labelFont;
            ctx.fillStyle = "#8b95a1";
            ctx.textAlign = "left";
            ctx.fillText(info.label, PADDING_X, y + 5);

            //값 (오른쪽 정렬, 여러 줄 가능)
            ctx.font = valueFont;
            ctx.fillStyle = "#191f28";
            ctx.textAlign = "right";
            info.lines.forEach((line, index) => {
                ctx.fillText(line, WIDTH - PADDING_X, y + 5 + index * LINE_HEIGHT);
            });

            y += rowHeight - 20;

            //행 구분선
            ctx.strokeStyle = "#f2f4f6";
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(PADDING_X, y);
            ctx.lineTo(WIDTH - PADDING_X, y);
            ctx.stroke();

            y += 20;
        });
    };

    drawSectionHeader("결제정보");
    drawRows(paymentRowInfos);

    if (businessRowInfos.length > 0) {
        y += sectionGap - 20;
        drawSectionHeader("사업자정보");
        drawRows(businessRowInfos);
    }

    //절취선(지그재그)
    y += zigzagGap - 20;

    ctx.strokeStyle = "#e9ecef";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    for (let x = 0; x <= WIDTH; x += 14) {
        ctx.lineTo(x + 7, y + 5);
        ctx.lineTo(x + 14, y);
    }
    ctx.stroke();

    //하단 결제선생 로고 (로드 실패 시 회색 텍스트로 대체)
    const logoImg = await loadImage(logoUrl);

    if (logoImg && logoImg.naturalWidth > 0) {
        const logoWidth = 88;
        const logoHeight = logoWidth * (logoImg.naturalHeight / logoImg.naturalWidth);
        ctx.drawImage(logoImg, (WIDTH - logoWidth) / 2, y + 34, logoWidth, logoHeight);
    } else {
        ctx.font = `700 15px ${FONT_STACK}`;
        ctx.fillStyle = "rgba(26, 26, 26, 0.2)";
        ctx.textAlign = "center";
        ctx.fillText("결제선생", WIDTH / 2, y + 46);
    }

    const dataUrl = canvas.toDataURL("image/png");

    const blob = await new Promise<Blob | null>((resolve) => {
        try {
            canvas.toBlob((result) => resolve(result), "image/png");
        } catch (e) {
            resolve(null);
        }
    });

    return { dataUrl, blob };
};
